/**
 * TECNILAND - API Service
 * Centraliza todas las llamadas al backend
 * Endpoints de autenticación y skins
 * OPTIMIZED: console.error solo en desarrollo
 * PRODUCTION-READY: Usa variables de entorno
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://tecniland-backend.fly.dev';
const IS_PROD = process.env.NODE_ENV === 'production';

// Helper para logging seguro
const logError = (message, error) => {
  if (!IS_PROD) console.error(message, error);
};

/**
 * Obtiene los headers con autorización
 * @returns {Object} Headers con token Bearer
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Maneja errores de respuesta API
 * @param {Response} response - Respuesta fetch
 * @param {Object} data - Datos de respuesta
 * @returns {boolean} true si hay error crítico que requiere logout
 */
export const handleApiError = (response, data) => {
  if (response.status === 401 || data.code === 'INVALID_TOKEN') {
    // Token expirado o inválido
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    window.location.href = '/';
    return true;
  }
  return false;
};

/**
 * Servicio de API para el backend de TECNILAND
 */
export const apiService = {
  // ============ AUTENTICACIÓN ============

  /**
   * Obtiene el perfil del usuario autenticado
   * @returns {Promise<Object>} Datos del perfil
   */
  async getProfile() {
    try {
      const response = await fetch(`${API_BASE}/api/auth/profile`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (handleApiError(response, data)) {
        return { success: false, error: 'Sesión expirada' };
      }
      
      return data;
    } catch (error) {
      logError('Error obteniendo perfil:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Cierra la sesión actual
   * @returns {Promise<Object>} Confirmación
   */
  async logout() {
    try {
      const response = await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return response.json();
    } catch (error) {
      logError('Error en logout:', error);
      return {
        success: false,
        error: 'Error de conexión',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Cierra todas las sesiones del usuario
   * @returns {Promise<Object>} Número de sesiones cerradas
   */
  async logoutAll() {
    try {
      const response = await fetch(`${API_BASE}/api/auth/sessions`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return response.json();
    } catch (error) {
      logError('Error cerrando sesiones:', error);
      return {
        success: false,
        error: 'Error de conexión',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  // ============ SKINS ============

  /**
   * Obtiene la skin actual del usuario
   * @returns {Promise<Object>} Datos de la skin
   */
  async getMySkin() {
    try {
      const response = await fetch(`${API_BASE}/api/skins/me`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (handleApiError(response, data)) {
        return { success: false, error: 'Sesión expirada' };
      }
      
      return data;
    } catch (error) {
      logError('Error obteniendo skin:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Sube una nueva skin
   * @param {File} file - Archivo PNG de la skin
   * @param {string} model - Modelo: 'steve' o 'alex'
   * @returns {Promise<Object>} URL de la skin subida
   */
  async uploadSkin(file, model = 'steve') {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('skin', file);
      formData.append('model', model);

      const response = await fetch(`${API_BASE}/api/skins/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // No incluir Content-Type, el navegador lo genera con boundary
        },
        body: formData
      });

      const data = await response.json();
      
      if (handleApiError(response, data)) {
        return { success: false, error: 'Sesión expirada' };
      }
      
      return data;
    } catch (error) {
      logError('Error subiendo skin:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Elimina la skin actual del usuario
   * @returns {Promise<Object>} Confirmación
   */
  async deleteSkin() {
    try {
      const response = await fetch(`${API_BASE}/api/skins/delete`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (handleApiError(response, data)) {
        return { success: false, error: 'Sesión expirada' };
      }
      
      return data;
    } catch (error) {
      logError('Error eliminando skin:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  // ============ ACTUALIZACIÓN DE PERFIL ============

  /**
   * Cambia la contraseña del usuario
   * @param {string} currentPassword - Contraseña actual
   * @param {string} newPassword - Nueva contraseña
   * @returns {Promise<Object>} Confirmación
   */
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ currentPassword, newPassword })
      });
      const data = await response.json();
      
      // No usamos handleApiError porque después del cambio el token será inválido
      return data;
    } catch (error) {
      logError('Error cambiando contraseña:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Cambia el email del usuario
   * @param {string} newEmail - Nuevo email
   * @returns {Promise<Object>} Confirmación con nuevo email
   */
  async changeEmail(newEmail) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/email`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ newEmail })
      });
      const data = await response.json();
      
      if (handleApiError(response, data)) {
        return { success: false, error: 'Sesión expirada' };
      }
      
      return data;
    } catch (error) {
      logError('Error cambiando email:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Cambia el username del usuario
   * @param {string} newUsername - Nuevo username
   * @returns {Promise<Object>} Confirmación con nuevo username
   */
  async changeUsername(newUsername) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/username`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ newUsername })
      });
      const data = await response.json();
      
      if (handleApiError(response, data)) {
        return { success: false, error: 'Sesión expirada' };
      }
      
      return data;
    } catch (error) {
      logError('Error cambiando username:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Verifica disponibilidad de un username
   * @param {string} username - Username a verificar
   * @returns {Promise<Object>} Disponibilidad
   */
  async checkUsernameAvailability(username) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/username/check/${encodeURIComponent(username)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      return response.json();
    } catch (error) {
      logError('Error verificando username:', error);
      return {
        success: false,
        error: 'Error de conexión',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  // ============ RECUPERACIÓN DE CONTRASEÑA ============

  /**
   * Solicita recuperación de contraseña por email
   * @param {string} email - Email del usuario
   * @returns {Promise<Object>} Confirmación
   */
  async forgotPassword(email) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      return response.json();
    } catch (error) {
      logError('Error solicitando recuperación:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  /**
   * Restablece la contraseña con token
   * @param {string} token - Token de recuperación
   * @param {string} newPassword - Nueva contraseña
   * @returns {Promise<Object>} Confirmación
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword })
      });
      return response.json();
    } catch (error) {
      logError('Error restableciendo contraseña:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  },

  // ============ ELIMINACIÓN DE CUENTA ============

  /**
   * Elimina permanentemente la cuenta del usuario
   * @param {string} confirmPassword - Contraseña actual del usuario
   * @returns {Promise<Object>} Confirmación
   */
  async deleteAccount(confirmPassword) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return {
          success: false,
          error: 'No estás autenticado',
          code: 'UNAUTHORIZED'
        };
      }

      const url = `${API_BASE}/api/auth/account`;
      
      // Solo log en desarrollo
      if (!IS_PROD) {
        console.log('[deleteAccount] URL:', url);
      }

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ confirmPassword })
      });

      const data = await response.json();
      
      return data;
    } catch (error) {
      logError('Error eliminando cuenta:', error);
      return {
        success: false,
        error: 'Error de conexión con el servidor',
        code: 'CONNECTION_ERROR'
      };
    }
  }
};

export default apiService;
